import { useState } from 'react'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Pagination from '@mui/material/Pagination'
import CircularProgress from '@mui/material/CircularProgress'
import { useTranslation } from 'react-i18next'
import { useOrderHistory } from '../shared/api/orders'
import { useStaffList } from '../shared/api/users'
import { useOwnerAuth } from './OwnerAuthContext'
import { formatMoney } from '../shared/money'
import { formatDateTime } from '../shared/date'
import type { Order } from '../shared/types'

// Status → chip colour, matching the live board's pipeline cues; terminal states are muted.
const STATUS_COLOR: Record<string, 'info' | 'warning' | 'success' | 'default' | 'error'> = {
  NEW: 'info',
  PREPARING: 'warning',
  READY: 'success',
  DELIVERED: 'default',
  CANCELLED: 'error',
}

/** Owner order history (§16 Phase 6b): all orders, any status, newest first, paginated. */
export default function OrderHistoryPage() {
  const { t } = useTranslation()
  const { isAuthenticated } = useOwnerAuth()
  const [page, setPage] = useState(0) // 0-based for the API
  const { data, isLoading, isError, isPlaceholderData } = useOrderHistory(page, isAuthenticated)

  return (
    <Container maxWidth="md" sx={{ py: 2, pb: 8 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        {t('owner.history.title')}
      </Typography>

      {isLoading ? (
        <CircularProgress />
      ) : isError ? (
        <Typography color="error">{t('owner.history.loadError')}</Typography>
      ) : !data || data.content.length === 0 ? (
        <Typography color="text.secondary">{t('owner.history.noOrders')}</Typography>
      ) : (
        <>
          <Stack spacing={1} sx={{ opacity: isPlaceholderData ? 0.6 : 1, transition: 'opacity .2s' }}>
            {data.content.map((order) => (
              <HistoryRow key={order.id} order={order} />
            ))}
          </Stack>
          {data.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={data.totalPages}
                page={page + 1} // MUI is 1-based
                onChange={(_, value) => setPage(value - 1)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  )
}

function HistoryRow({ order }: { order: Order }) {
  const { t } = useTranslation()
  // Resolve the delivery assignee's id → name. One shared query key, so N rows still make one
  // request; only fetched when this order actually has an assignee. Name falls back to email (D-076).
  const { data: staff } = useStaffList(!!order.assignedStaffId)
  const deliverer = staff?.find((u) => u.id === order.assignedStaffId)
  const deliveredBy = deliverer?.name ?? deliverer?.email
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography fontWeight={700}>#{order.orderNumber}</Typography>
              <Chip
                label={t(`status.${order.status}`, { defaultValue: order.status })}
                size="small"
                color={STATUS_COLOR[order.status] ?? 'default'}
              />
              {order.fulfillmentType === 'PICKUP' && (
                <Chip label={t('owner.fulfillment.pickup')} size="small" color="warning" variant="outlined" />
              )}
              {order.channel === 'POS' && <Chip label="POS" size="small" variant="outlined" />}
            </Stack>
            <Typography variant="body2" color="text.secondary" noWrap>
              {order.customerName} · {order.customerPhone}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(order.createdAt)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            {/* Priceless order (D-069): null total → no money, keep the payment caption. */}
            {order.total != null && <Typography fontWeight={700}>{formatMoney(order.total)}</Typography>}
            <Typography variant="caption" color="text.secondary">
              {order.paymentMethod} · {order.paymentStatus}
            </Typography>
          </Box>
        </Stack>

        {/* What was actually ordered — the snapshotted names/prices, not the live product rows (§12). */}
        <Divider sx={{ my: 1 }} />
        <Stack spacing={0.5}>
          {order.items.map((item, index) => (
            <Stack
              key={index}
              direction="row"
              justifyContent="space-between"
              spacing={1}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5 }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {item.quantity} × {item.productName}
                </Typography>
                {item.options && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {item.options}
                  </Typography>
                )}
                {/* Per-line leg tag / token (D-102). */}
                {item.tagNumber && (
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'primary.main' }}>
                    {t('owner.board.tagNumber')}: {item.tagNumber}
                  </Typography>
                )}
              </Box>
              {item.lineTotal != null && (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {formatMoney(item.lineTotal)}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
        {order.deliveryAddress && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>{t('owner.history.address')}:</strong> {order.deliveryAddress}
          </Typography>
        )}
        {order.notes && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
            {t('owner.history.notes')}: {order.notes}
          </Typography>
        )}
        {deliveredBy && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            <strong>{t('owner.history.deliveredBy')}:</strong> {deliveredBy}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}
